import { FLOWS_PRODUCTS_PATH } from '@flows/src/FlowsProductsRouter';
import { useLocale } from '../../../../../i18n';
import useAuth from '../../../../app/src/features/authentication/hooks/useAuth';
import { getFlowInfo } from '../../../../flows/src/api/getFlowInfo';
import useFlowsInitNew from '../../../../flows/src/hooks/init/useFlowsInitNew';
import useFlowsResetState from '../../../../flows/src/hooks/useFlowsResetState';
import { FlowInfo } from '../../../../flows/src/types/FlowsTypes';

export interface AnvaFlowProduct {
  id: string;
  coverages: string[];
}

const useStartAnvaFlow = () => {
  const { initFlow } = useFlowsInitNew();
  const { userLang } = useLocale();
  const { campaign } = useAuth();
  const { reset } = useFlowsResetState();

  /**
   * Builds the URL the user is redirected to once the questions part of the
   * flow is completed.
   *
   * For ANVA flows we also need to pass the session id as a query parameter so
   * that the product-selection pages know which session to use when fetching
   * the quotes. Without this parameter the page loads without triggering the
   * calculations, which is the bug we're fixing here.
   */
  const getOutputUrl = (flowInfo: FlowInfo, sessionId?: string) => {
    if (flowInfo.sub_type === 'ANVA') {
      const base = `${FLOWS_PRODUCTS_PATH}/${flowInfo.id}/overview`;
      // Only append the param when we actually have a session id – otherwise we
      // keep the original behaviour.
      return sessionId ? `${base}?session_id=${sessionId}` : base;
    }
    return `/sales-conversation/default/finalize`;
  };

  return async ({
    flowId,
    uuid,
    inputUrl,
    partyId,
    quoteId,
    quoteIds,
    anva_products,
  }: {
    flowId: string;
    anva_products: AnvaFlowProduct[];
    uuid?: string;
    inputUrl?: string;
    partyId?: string;
    quoteId?: string;
    quoteIds?: string[];
  }) => {
    reset();

    const flowInfo = await getFlowInfo(flowId, { lang: userLang });

    await initFlow({
      flowId: flowId,
      payload: {
        party_id: partyId,
        quote_id: quoteId,
        quote_ids: quoteIds,
        previous_session_id: flowInfo.previous ? uuid : undefined,
        subject: campaign?.subjects?.length ? campaign.subjects : undefined,
        anva_products: anva_products,
      },
      inputUrl,
      outputUrl: getOutputUrl(flowInfo, uuid),
      sessionId: uuid,
      shouldComplete: false,
    });
  };
};

export default useStartAnvaFlow;
